import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { LinkConfigurationStepConfirmation } from '@app/api';
import { LinkConfigurationCheckSection } from './LinkConfigurationCheckSection';
import { FormikValues, useFormikContext } from 'formik';
import { LinkConfigurationCheckElement } from './LinkConfigurationCheckElement';
import { ToggleGroup } from 'components/ToggleGroup';
import { LabeledSection } from '@app/core';
import { Input } from '@app/form';
import { LinkConfigurationMainBlock } from './LinkConfigurationMainBlock';

const useStyles = createUseStyles((theme: Theme) => ({
  text: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-wrap',
  },
  link: {
    width: '100%',
  },
  error: {
    paddingTop: theme.marginBase,
    ...theme.fonts.caption,
    color: theme.colors.red,
  },
}));

const components = { b: <strong /> };

export const MatchingParticipants = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { values, setFieldValue, errors } = useFormikContext<FormikValues>();

  const [selectedLinkConfigurationStep, setSelectedLinkConfigurationStep] =
    useState<LinkConfigurationStepConfirmation | null>(
      values.linkConfigurationStepConfirmation?.find(
        (step: string) =>
          step === LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_ID ||
          step === LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_URL,
      ) || null,
    );

  const options = useMemo(() => {
    return [
      {
        value: LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_URL,
        label: t(
          'editSession.linkConfiguration.studyLinkConfiguration.buttonLabel.MATCHING_PARTICIPANT_URL',
        ),
      },
      {
        value: LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_ID,
        label: t(
          'editSession.linkConfiguration.studyLinkConfiguration.buttonLabel.MATCHING_PARTICIPANT_ID',
        ),
      },
    ];
  }, [t]);

  const fakeTid = 'demoTid32e';
  const urlReplaced = useMemo(() => {
    let finalUrl = values.studyToolLinks?.[0]?.studyToolLink as string;
    if (!finalUrl) {
      return finalUrl;
    }
    if (!finalUrl.startsWith('http')) {
      finalUrl = `https://${finalUrl}`;
    }

    const replaceRegex = new RegExp(
      '(%|(\\{\\{)|(__))((SPM_PANELIST_ID)|(TID)|(TRACKING_?ID)|(TANDEMZ_?ID))(%|(\\}\\})|(__))',
      'gi',
    );

    if (replaceRegex.test(finalUrl)) {
      finalUrl = finalUrl.replace(replaceRegex, fakeTid);
    } else {
      try {
        const urlObj = new URL(finalUrl);
        urlObj.searchParams.set('tid', fakeTid);
        finalUrl = urlObj.toString();
      } catch (e) {
        return finalUrl;
      }
    }

    return finalUrl;
  }, [values, fakeTid]);

  return (
    <LinkConfigurationCheckSection
      title={t(
        'editSession.linkConfiguration.studyLinkConfiguration.matchingParticipants.title',
      )}
      help={
        <Trans
          i18nKey="editSession.linkConfiguration.studyLinkConfiguration.matchingParticipants.help"
          components={components}
        />
      }
    >
      <LabeledSection
        color="ink"
        label={t(
          'editSession.linkConfiguration.studyLinkConfiguration.matchingParticipants.question',
        )}
      >
        <ToggleGroup
          options={options}
          selectedValue={selectedLinkConfigurationStep as string}
          onChange={(name) => {
            setSelectedLinkConfigurationStep(
              name as LinkConfigurationStepConfirmation,
            );
            setFieldValue(
              'linkConfigurationStepConfirmation',
              values.linkConfigurationStepConfirmation.filter(
                (step: string) =>
                  step !==
                    LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_ID &&
                  step !==
                    LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_URL,
              ),
            );
          }}
          color="green"
        />
        {errors.linkConfigurationStepConfirmation &&
          !selectedLinkConfigurationStep && (
            <p className={classes.error}>
              {t('form.errors.linkConfigurationStepConfirmation.default')}
            </p>
          )}
      </LabeledSection>

      {selectedLinkConfigurationStep ===
      LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_URL ? (
        <LinkConfigurationMainBlock
          text={
            <Trans
              i18nKey="editSession.linkConfiguration.studyLinkConfiguration.matchingParticipants.info.url"
              components={components}
            />
          }
          link="?tid={{tracking_id}}"
          helpUrl={t(
            'editSession.linkConfiguration.studyLinkConfiguration.linkHelp',
          )}
        >
          <p className={classes.text}>
            <Trans
              i18nKey="editSession.linkConfiguration.studyLinkConfiguration.matchingParticipants.templateExplanation"
              values={{ tid: fakeTid }}
              components={components}
            />
          </p>
          <Input
            className={classes.link}
            value={urlReplaced}
            disabled
            noMargin
          />
        </LinkConfigurationMainBlock>
      ) : (
        <LinkConfigurationMainBlock
          text={
            <Trans
              i18nKey="editSession.linkConfiguration.studyLinkConfiguration.matchingParticipants.info.id"
              components={components}
            />
          }
        />
      )}
      {selectedLinkConfigurationStep && (
        <LinkConfigurationCheckElement
          linkConfigurationStep={selectedLinkConfigurationStep}
        />
      )}
    </LinkConfigurationCheckSection>
  );
};
